const DEFAULT_MAX_WIDTH = 'sm:max-w-lg';
let sharedModalState = {
    open: false,
    detail: {},
    closeOnSubmit: true,
    autoRefresh: false,
    isRefreshing: false,
};

function icon(name, extraClass = '') {
    return `<span class="icon mc-icon-body ${extraClass}" aria-hidden="true"><i class="ci ci-${name}" aria-hidden="true"></i></span>`;
}

function ensureModalShell() {
    let shell = document.getElementById('shared-htmx-modal');
    if (shell) return shell;

    shell = document.createElement('aside');
    shell.id = 'shared-htmx-modal';
    shell.hidden = true;
    shell.innerHTML = `
        <div class="mo-wrap" role="presentation">
            <div class="mo-back" data-shared-modal-close></div>
            <div class="mo-box" role="dialog" aria-modal="true" aria-labelledby="shared-htmx-modal-title" tabindex="-1">
                <div class="mo-head mo-head--mb" data-shared-modal-head>
                    <h3 id="shared-htmx-modal-title" class="mo-title" data-shared-modal-title></h3>
                    <button type="button" class="mo-close" aria-label="Close modal" data-shared-modal-close>
                        ${icon('xmark')}
                    </button>
                </div>
                <div id="shared-htmx-modal-content" class="mo-body"></div>
            </div>
        </div>
    `;

    shell.addEventListener('click', event => {
        if (event.target.closest('[data-shared-modal-close]')) closeSharedHtmxModal();
    });

    document.body.appendChild(shell);
    return shell;
}

function getEls() {
    const shell = ensureModalShell();
    return {
        shell,
        box: shell.querySelector('.mo-box'),
        head: shell.querySelector('[data-shared-modal-head]'),
        title: shell.querySelector('[data-shared-modal-title]'),
        close: shell.querySelector('.mo-close'),
        content: shell.querySelector('.mo-body'),
    };
}

function loadingHtml() {
    return `<div class="mo-load">${icon('spinner', 'mo-spin')}<span>Loading...</span></div>`;
}

function errorHtml() {
    return `<div class="mo-load text-error">${icon('circle-exclamation')}<span>Failed to load content</span></div>`;
}

function normalizeDetail(detail = {}) {
    return {
        title: detail.title || '',
        modal_id: detail.modal_id || 'shared',
        htmx_url: detail.htmx_url || window.location.pathname,
        htmx_method: (detail.htmx_method || 'post').toLowerCase(),
        htmx_vals: detail.htmx_vals || {},
        max_width: detail.max_width || DEFAULT_MAX_WIDTH,
        show_close_button: detail.show_close_button !== false,
        auto_refresh_on_submit: detail.auto_refresh_on_submit === true,
        close_on_submit: detail.close_on_submit !== false,
    };
}

function applyChrome(detail) {
    const { shell, box, head, title, close, content } = getEls();
    const hasHead = detail.show_close_button || detail.title;

    box.className = `mo-box ${detail.max_width}`.trim();
    head.hidden = !hasHead;
    title.hidden = !detail.title;
    title.textContent = detail.title;
    close.hidden = !detail.show_close_button;
    content.id = detail.modal_id === 'shared' ? 'shared-htmx-modal-content' : `htmx-modal-content-${detail.modal_id}`;
    content.innerHTML = loadingHtml();
    shell.hidden = false;
    document.body.style.overflow = 'hidden';

    const focusTarget = detail.show_close_button ? close : box;
    focusTarget.focus({ preventScroll: true });
}

function makeUrl(detail) {
    if (detail.htmx_method !== 'get') return detail.htmx_url;

    const url = new URL(detail.htmx_url, window.location.origin);
    Object.entries(detail.htmx_vals || {}).forEach(([key, value]) => {
        url.searchParams.set(key, typeof value === 'string' ? value : JSON.stringify(value));
    });
    return url.toString();
}

function loadSharedHtmxModalContent(detail) {
    const { content } = getEls();
    const method = detail.htmx_method.toUpperCase();

    return fetch(makeUrl(detail), {
        method,
        headers: {
            'Content-Type': 'application/json',
            'HX-Request': 'true',
        },
        body: method === 'GET' ? undefined : JSON.stringify(detail.htmx_vals),
    })
        .then(response => response.text())
        .then(html => {
            content.innerHTML = html;
            if (typeof htmx !== 'undefined') htmx.process(content);
        })
        .catch(() => {
            content.innerHTML = errorHtml();
        })
        .finally(() => {
            sharedModalState.isRefreshing = false;
        });
}

function openSharedHtmxModal(detail) {
    const normalized = normalizeDetail(detail);
    sharedModalState = {
        open: true,
        detail: normalized,
        closeOnSubmit: normalized.close_on_submit,
        autoRefresh: normalized.auto_refresh_on_submit,
        isRefreshing: false,
    };
    applyChrome(normalized);
    return loadSharedHtmxModalContent(normalized);
}

function closeSharedHtmxModal() {
    if (!sharedModalState.closeOnSubmit && sharedModalState.isRefreshing) return;

    const { shell, content, title } = getEls();
    sharedModalState.open = false;
    shell.hidden = true;
    document.body.style.overflow = '';
    setTimeout(() => {
        if (!sharedModalState.open) {
            content.id = 'shared-htmx-modal-content';
            content.innerHTML = '';
            title.textContent = '';
        }
    }, 300);
}

function refreshSharedHtmxModal(detail = sharedModalState.detail) {
    if (!sharedModalState.open) return Promise.resolve();
    sharedModalState.isRefreshing = true;
    return loadSharedHtmxModalContent(normalizeDetail(detail));
}

function readTriggerConfig(button) {
    try {
        return JSON.parse(button.dataset.modalConfig || '{}');
    } catch {
        return {};
    }
}

document.addEventListener('click', event => {
    const button = event.target.closest('[data-shared-htmx-modal-trigger]');
    if (!button) return;

    event.preventDefault();
    openSharedHtmxModal(readTriggerConfig(button));
});

document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && sharedModalState.open) closeSharedHtmxModal();
});

document.addEventListener('htmx:afterRequest', event => {
    if (!sharedModalState.open || !sharedModalState.autoRefresh) return;

    const { content } = getEls();
    if (!content.contains(event.detail.target) || !event.detail.successful) return;

    if (sharedModalState.closeOnSubmit) {
        closeSharedHtmxModal();
        return;
    }

    setTimeout(() => refreshSharedHtmxModal(), 300);
});

window.addEventListener('open-htmx-modal', event => openSharedHtmxModal(event.detail));
window.addEventListener('close-htmx-modal', closeSharedHtmxModal);
window.addEventListener('close-modal', closeSharedHtmxModal);
window.addEventListener('refresh-htmx-modal', event => refreshSharedHtmxModal(event.detail));

function sharedHtmxModal() {
    return {
        openModal: openSharedHtmxModal,
        closeModal: closeSharedHtmxModal,
        refreshContent: refreshSharedHtmxModal,
    };
}

window.sharedHtmxModal = sharedHtmxModal;
window.openSharedHtmxModal = openSharedHtmxModal;
window.closeSharedHtmxModal = closeSharedHtmxModal;
